import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Login, reset } from "../fitur/AuthSlice";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  Button,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";

import { Eye, EyeSlash, Lock } from "@phosphor-icons/react";
import axios from "axios";
import useSWR from 'swr';
import BackgroundImage from '../login.jpg';
import { useBranding, useDynamicFavicon } from '../hooks/useBranding';
import { apiHost } from '../utils/apiHost';
import { bacaIngatLogin, simpanIngatLogin, lupakanLogin } from '../utils/ingatLogin';

const MySwal = withReactContent(Swal);

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = url => axios.get(url).then(res => res.data);

export const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isManualLogin, setIsManualLogin] = useState(false);
  const [akunTersimpan, setAkunTersimpan] = useState(false);

  const { user, isError, isSuccess, isLoading, message } = useSelector((state) => state.auth);
  const { data, error } = useSWR(`${getApiBaseUrl()}/`, fetcher);
  const { logoUrl } = useBranding();
  useDynamicFavicon();
 const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Remember me hanya mengingat username (sandi tidak pernah disimpan; sandi lama dari versi sebelumnya dihapus otomatis)
  useEffect(() => {
    const tersimpan = bacaIngatLogin(localStorage);
    if (tersimpan.ingat) {
      setUsername(tersimpan.username);
      setRememberMe(true);
      setAkunTersimpan(true);
    }
  }, []);

  const gantiAkun = () => {
    lupakanLogin(localStorage);
    setUsername("");
    setPassword("");
    setRememberMe(false);
    setAkunTersimpan(false);
  };

  // Handle login response
  useEffect(() => {
    if (isSuccess && user) {
      const userData = user.data || user;
      const userRole = userData.role;
      Swal.fire({
        icon: "success",
        title: "Login Berhasil!",
        text: `Selamat datang kembali ${userData.username}`,
        showConfirmButton: false,
        timer: 2000,
      });

      if (userRole === "admin" || userRole === "superadmin") {
        navigate("/dashboard", { replace: true });
      } else if (userRole === "kasir") {
        navigate("/produkpercabang", { replace: true });
      } else if (userRole === "karyawan") {
        navigate("/dashboardkaryawan", { replace: true });
      } else if (userRole === "dapur") {
        navigate("/dapur", { replace: true });
      }
      setIsManualLogin(false);
    } else if (isError && message && isManualLogin) {
      Swal.fire({
        icon: "error",
        title: "Login Gagal!",
        text: message,
        confirmButtonText: "OK",
      });
      dispatch(reset());
      setIsManualLogin(false);
    }
  }, [isSuccess, isError, message, user, navigate, dispatch, isManualLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      Swal.fire({
        icon: "warning",
        title: "Peringatan",
        text: "Username dan password wajib diisi!",
      });
      return;
    }
  
    simpanIngatLogin(localStorage, { username, ingat: rememberMe });
  
    dispatch(reset());
    setIsManualLogin(true);
    try {
      await dispatch(Login({ username, password }));
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          height: "auto",
        //  backgroundImage: `url(${BackgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          padding: isMobile ? 2 : 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: isMobile ? 3 : 4,
            width: "100%",
            maxWidth: 380,
            textAlign: "center",
            borderRadius: 3,
            bgcolor: "rgba(255, 255, 255, 0.95)",
            margin: "auto",
          }}
        >
          {/* Logo */}
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo Aplikasi"
              style={{
                width: isMobile ? "60px" : "80px",
                maxHeight: isMobile ? "60px" : "80px",
                objectFit: "contain",
                marginBottom: isMobile ? "8px" : "10px"
              }}
            />
          )}
          <Typography
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom
            sx={{ fontSize: isMobile ? "0.9rem" : "1.25rem" }}
          >
Kasir
          </Typography>

          {/* Status Server */}
          {isError && (
            <Typography color="error" fontSize={isMobile ? "0.8rem" : "1rem"}>
              {message}
            </Typography>
          )}
          {error ? (
            <Typography color="error" fontSize={isMobile ? "0.8rem" : "1rem"}>
              Server Offline
            </Typography>
          ) : data ? (
            <Typography color="success" fontSize={isMobile ? "0.8rem" : "1rem"}>
              Server Online: {data}
            </Typography>
          ) : (
            <Typography fontSize={isMobile ? "0.8rem" : "1rem"}>
              Checking server status...
            </Typography>
          )}

          {/* Form */}
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              autoComplete="username"
              margin="normal"
              variant="filled"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              margin="normal"
              variant="filled"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <Eye size={isMobile ? 16 : 18} /> : <EyeSlash size={isMobile ? 16 : 18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography fontSize={isMobile ? "0.8rem" : "1rem"}>
                  Ingat username saya
                </Typography>
              }
              sx={{ mt: 0.5 }}
            />

            {akunTersimpan && (
              <Button type="button" size="small" onClick={gantiAkun} sx={{ textTransform: "none", mt: 0.5 }}>
                Bukan Anda? Ganti akun
              </Button>
            )}

            {/* Tombol Login */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: isMobile ? 1 : 1.5,
              }}
            >
              <Lock size={isMobile ? 12 : 14} /> {isLoading ? "Loading..." : "Login"}
            </Button>

            {/* Tombol Reset Password */}
            {/* <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => navigate("/reset-password")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mt: 1, 
                textTransform: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem"
              }}
            >
              Lupa Password? Reset di sini
            </Button> */}
            
          </form>
           
        </Paper>
      </Box>
    
    </>
  );
};